import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
  Filler,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { colormaps, defaultColormap } from "./colormaps";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  Filler
);

const isFiniteNum = (v) => Number.isFinite(Number(v));

function clamp(v, a, b) {
  return Math.min(Math.max(v, a), b);
}

function buildContinuousGuideCurve(xs, ys) {
  if (!xs?.length || !ys?.length) return [];

  const out = new Array(ys.length).fill(null);

  for (let i = 0; i < ys.length; i++) {
    const y = ys[i];
    out[i] = y != null && Number.isFinite(Number(y)) ? Number(y) : null;
  }

  let firstValid = -1;
  for (let i = 0; i < out.length; i++) {
    if (out[i] != null) {
      firstValid = i;
      break;
    }
  }
  if (firstValid === -1) return [];

  for (let i = 0; i < firstValid; i++) out[i] = out[firstValid];

  let lastValid = -1;
  for (let i = out.length - 1; i >= 0; i--) {
    if (out[i] != null) {
      lastValid = i;
      break;
    }
  }
  for (let i = lastValid + 1; i < out.length; i++) out[i] = out[lastValid];

  let i = firstValid;
  while (i < out.length) {
    if (out[i] != null) {
      i++;
      continue;
    }

    const start = i - 1;
    let end = i;
    while (end < out.length && out[end] == null) end++;
    if (end >= out.length) break;

    const x1 = Number(xs[start]);
    const x2 = Number(xs[end]);
    const y1 = out[start];
    const y2 = out[end];
    const span = x2 - x1 || 1;

    for (let k = i; k < end; k++) {
      const x = Number(xs[k]);
      const t = (x - x1) / span;
      out[k] = y1 + (y2 - y1) * t;
    }

    i = end;
  }

  const smoothPass = (arr) => {
    const next = [...arr];
    for (let j = 1; j < arr.length - 1; j++) {
      next[j] = (arr[j - 1] + arr[j] + arr[j + 1]) / 3;
    }
    return next;
  };

  let smoothed = out.map((v) => Number(v));
  smoothed = smoothPass(smoothed);
  smoothed = smoothPass(smoothed);

  return xs.map((x, idx) => ({
    x: Number(x),
    y: smoothed[idx],
  }));
}

function interpolateOnCurve(points, xTarget) {
  if (!points?.length || !Number.isFinite(Number(xTarget))) return null;
  if (points.length === 1) return { x: xTarget, y: points[0].y };

  if (xTarget <= points[0].x) return { x: xTarget, y: points[0].y };
  if (xTarget >= points[points.length - 1].x) {
    return { x: xTarget, y: points[points.length - 1].y };
  }

  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];

    const minX = Math.min(p1.x, p2.x);
    const maxX = Math.max(p1.x, p2.x);

    if (xTarget >= minX && xTarget <= maxX) {
      const span = p2.x - p1.x || 1;
      const t = (xTarget - p1.x) / span;
      return {
        x: xTarget,
        y: p1.y + (p2.y - p1.y) * t,
      };
    }
  }

  return { x: xTarget, y: points[points.length - 1].y };
}

function makeCursorBallPlugin() {
  return {
    id: "cursorBallPlugin",
    afterDatasetsDraw(chart, _args, pluginOptions) {
      const {
        cursorX = null,
        cursorEnvelopePoint = null,
        ballRadius = 12,
        showBall = true,
        isMobile = false,
      } = pluginOptions || {};

      if (!showBall || cursorX == null) return;

      const { ctx, chartArea, scales } = chart;
      const xScale = scales?.x;
      const yScale = scales?.y;
      if (!ctx || !chartArea || !xScale || !yScale) return;

      const { left, right, top, bottom } = chartArea;

      const xC = xScale.getPixelForValue(cursorX);
      if (Number.isFinite(xC) && xC >= left && xC <= right) {
        ctx.save();

        ctx.strokeStyle = "rgba(255,255,255,0.90)";
        ctx.lineWidth = isMobile ? 3 : 3.5;
        ctx.beginPath();
        ctx.moveTo(xC, top);
        ctx.lineTo(xC, bottom);
        ctx.stroke();

        ctx.strokeStyle = "rgba(16,185,129,1)";
        ctx.lineWidth = isMobile ? 1.6 : 1.8;
        ctx.beginPath();
        ctx.moveTo(xC, top);
        ctx.lineTo(xC, bottom);
        ctx.stroke();

        ctx.restore();
      }

      if (
        !cursorEnvelopePoint ||
        !Number.isFinite(Number(cursorEnvelopePoint.x)) ||
        !Number.isFinite(Number(cursorEnvelopePoint.y))
      ) {
        return;
      }

      const r = ballRadius ?? (isMobile ? 8.5 : 12);
      const px = xScale.getPixelForValue(cursorEnvelopePoint.x);
      const py = yScale.getPixelForValue(cursorEnvelopePoint.y);

      if (!Number.isFinite(px) || !Number.isFinite(py)) return;
      if (px < left || px > right || py < top || py > bottom) return;

      ctx.save();

      ctx.shadowColor = "rgba(16,185,129,0.48)";
      ctx.shadowBlur = isMobile ? 14 : 18;
      ctx.beginPath();
      ctx.arc(px, py, r + 1.25, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(16,185,129,0.95)";
      ctx.fill();

      ctx.shadowBlur = 0;

      const bodyGrad = ctx.createRadialGradient(
        px - r * 0.32,
        py - r * 0.34,
        r * 0.18,
        px,
        py,
        r
      );
      bodyGrad.addColorStop(0, "rgba(220,252,231,1)");
      bodyGrad.addColorStop(0.2, "rgba(134,239,172,1)");
      bodyGrad.addColorStop(0.5, "rgba(34,197,94,1)");
      bodyGrad.addColorStop(0.8, "rgba(16,185,129,1)");
      bodyGrad.addColorStop(1, "rgba(4,120,87,1)");

      ctx.beginPath();
      ctx.arc(px, py, r, 0, Math.PI * 2);
      ctx.fillStyle = bodyGrad;
      ctx.fill();

      ctx.lineWidth = 2.4;
      ctx.strokeStyle = "rgba(255,255,255,0.92)";
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(
        px - r * 0.34,
        py - r * 0.38,
        Math.max(1.8, r * 0.26),
        0,
        Math.PI * 2
      );
      ctx.fillStyle = "rgba(255,255,255,0.88)";
      ctx.fill();

      ctx.beginPath();
      ctx.arc(
        px - r * 0.08,
        py - r * 0.14,
        Math.max(1.2, r * 0.11),
        0,
        Math.PI * 2
      );
      ctx.fillStyle = "rgba(255,255,255,0.32)";
      ctx.fill();

      ctx.beginPath();
      ctx.arc(
        px + r * 0.28,
        py + r * 0.34,
        Math.max(1.4, r * 0.18),
        0,
        Math.PI * 2
      );
      ctx.fillStyle = "rgba(0,0,0,0.10)";
      ctx.fill();

      ctx.restore();
    },
  };
}

const ColorBar = ({ colormap, min = 41, max = 86 }) => {
  const steps = 100;
  const gradientStops = [];

  for (let i = 0; i <= steps; i++) {
    const normalized = i / steps;
    const mappedIntensity = normalized * 90;
    const color =
      colormap?.getColor?.(mappedIntensity) || "rgb(100, 100, 100)";
    gradientStops.push(`${color} ${normalized * 100}%`);
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 text-xs text-gray-600">
        <span>{min}dB</span>
        <div
          className="flex-1 h-4 rounded"
          style={{
            background: `linear-gradient(to right, ${gradientStops.join(", ")})`,
          }}
        />
        <span>{max}dB</span>
      </div>
      <div className="flex justify-between text-xs text-gray-500 px-1">
        <span>50dB</span>
        <span>60dB</span>
        <span>70dB</span>
        <span>80dB</span>
      </div>
    </div>
  );
};

export default function PitchIntensityAnimationUser({
  intensity,
  pitch,
  audioRef,
  speed = 1,
  showPitch = true,
  selectedColormap = defaultColormap || "speechStable",
  onColormapChange,
}) {
  const [internalColormap, setInternalColormap] = useState(selectedColormap);
  const [isMobile, setIsMobile] = useState(false);
  const [animatedTime, setAnimatedTime] = useState(0);

  const rafRef = useRef(null);
  const displayTimeRef = useRef(0);

  const currentColormap = onColormapChange ? selectedColormap : internalColormap;

  const pitchXs = useMemo(() => pitch?.xs || [], [pitch]);
  const pitchYsRaw = useMemo(() => pitch?.ys || [], [pitch]);
  const intensityXs = useMemo(() => intensity?.xs || [], [intensity]);
  const intensityYs = useMemo(() => intensity?.ys || [], [intensity]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio) {
      displayTimeRef.current = 0;
      setAnimatedTime(0);
      return;
    }

    const syncNow = () => {
      const t = Math.max(0, audio.currentTime);
      displayTimeRef.current = t;
      setAnimatedTime(t);
    };

    const stop = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    const tick = () => {
      const target = Math.max(0, audio.currentTime);
      const prev = displayTimeRef.current ?? target;
      const smoothed = prev + (target - prev) * 0.35;

      displayTimeRef.current = smoothed;
      setAnimatedTime(smoothed);

      rafRef.current = requestAnimationFrame(tick);
    };

    const handleSeeked = () => {
      const t = Math.max(0, audio.currentTime);
      displayTimeRef.current = t;
      setAnimatedTime(t);
    };

    syncNow();

    audio.addEventListener("seeked", handleSeeked);
    audio.addEventListener("loadedmetadata", handleSeeked);
    audio.addEventListener("ratechange", handleSeeked);

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      stop();
      audio.removeEventListener("seeked", handleSeeked);
      audio.removeEventListener("loadedmetadata", handleSeeked);
      audio.removeEventListener("ratechange", handleSeeked);
    };
  }, [audioRef]);

  const handleColormapChange = useCallback(
    (newColormap) => {
      if (onColormapChange) {
        onColormapChange(newColormap);
      } else {
        setInternalColormap(newColormap);
      }
    },
    [onColormapChange]
  );

  const pitchYs = useMemo(() => {
    const out = new Array(pitchYsRaw.length);
    for (let i = 0; i < pitchYsRaw.length; i++) {
      const v = Number(pitchYsRaw[i]);
      out[i] = isFiniteNum(v) && v > 0 ? v : null;
    }
    return out;
  }, [pitchYsRaw]);

  const smoothedPitchYs = useMemo(() => {
    const windowSize = isMobile ? 2 : 3;
    const smoothed = [];

    for (let i = 0; i < pitchYs.length; i++) {
      if (pitchYs[i] == null) {
        smoothed.push(null);
        continue;
      }

      let sum = 0;
      let count = 0;

      for (
        let j = Math.max(0, i - windowSize);
        j <= Math.min(pitchYs.length - 1, i + windowSize);
        j++
      ) {
        if (pitchYs[j] != null) {
          sum += pitchYs[j];
          count++;
        }
      }

      smoothed.push(count ? sum / count : null);
    }

    return smoothed;
  }, [pitchYs, isMobile]);

  const voicedValues = useMemo(() => {
    return smoothedPitchYs.filter((v) => v != null && isFiniteNum(v));
  }, [smoothedPitchYs]);

  const yMin = useMemo(() => {
    let m = Infinity;
    for (let i = 0; i < voicedValues.length; i++) {
      m = Math.min(m, voicedValues[i]);
    }
    return Number.isFinite(m) ? m : 0;
  }, [voicedValues]);

  const yMax = useMemo(() => {
    let m = 1;
    for (let i = 0; i < voicedValues.length; i++) {
      m = Math.max(m, voicedValues[i]);
    }
    return m;
  }, [voicedValues]);

  const yRange = useMemo(() => Math.max(1, yMax - yMin), [yMax, yMin]);

  const { voicedXMin, voicedXMax } = useMemo(() => {
    if (!pitchXs.length || !smoothedPitchYs.length) {
      return { voicedXMin: 0, voicedXMax: 1 };
    }

    let first = -1;
    let last = -1;

    for (let i = 0; i < smoothedPitchYs.length; i++) {
      if (smoothedPitchYs[i] != null) {
        first = i;
        break;
      }
    }

    for (let i = smoothedPitchYs.length - 1; i >= 0; i--) {
      if (smoothedPitchYs[i] != null) {
        last = i;
        break;
      }
    }

    if (first === -1 || last === -1) {
      return {
        voicedXMin: pitchXs[0] ?? 0,
        voicedXMax: pitchXs[pitchXs.length - 1] ?? 1,
      };
    }

    return {
      voicedXMin: Number(pitchXs[first]),
      voicedXMax: Number(pitchXs[last]),
    };
  }, [pitchXs, smoothedPitchYs]);

  const cursorX = useMemo(() => {
    if (!pitchXs.length) return null;
    const t = Number(animatedTime);
    const raw = isFiniteNum(t) ? t : voicedXMin;
    return clamp(raw, voicedXMin, voicedXMax);
  }, [animatedTime, pitchXs.length, voicedXMin, voicedXMax]);

  const showBall = true;

  const predictedCursorX = useMemo(() => {
    if (!pitchXs.length) return null;

    const leadSeconds =
      speed <= 0.25 ? 0.01 :
      speed <= 0.5 ? 0.008 :
      0.005;

    return clamp(
      (cursorX ?? voicedXMin) + leadSeconds,
      voicedXMin,
      voicedXMax
    );
  }, [cursorX, pitchXs.length, voicedXMin, voicedXMax, speed]);

  const guideCurve = useMemo(() => {
    return buildContinuousGuideCurve(pitchXs, smoothedPitchYs);
  }, [pitchXs, smoothedPitchYs]);

  const cursorEnvelopePoint = useMemo(() => {
    if (!guideCurve.length || predictedCursorX == null) return null;

    const p = interpolateOnCurve(guideCurve, predictedCursorX);
    if (!p) return null;

    return {
      x: p.x,
      y: p.y,
    };
  }, [guideCurve, predictedCursorX]);

  const ballRadius = useMemo(() => {
    return isMobile ? 8.5 : 12;
  }, [isMobile]);

  const cursorBallPlugin = useMemo(() => makeCursorBallPlugin(), []);

  const data = useMemo(() => {
    return {
      datasets: [
        {
          label: "Pitch (Hz)",
          data: pitchXs.map((x, i) => ({ x, y: smoothedPitchYs[i] })),
          borderColor: "#2563eb",
          borderWidth: isMobile ? 1.75 : 2.25,
          tension: isMobile ? 0.25 : 0.42,
          cubicInterpolationMode: "monotone",
          pointRadius: 0,
          showLine: true,
          spanGaps: false,
          borderCapStyle: "round",
          borderJoinStyle: "round",
          fill: !!showPitch,
          backgroundColor: (context) => {
            if (!showPitch) return "rgba(0,0,0,0)";

            const chart = context.chart;
            const { ctx, chartArea } = chart;
            if (!chartArea) return null;

            const gradient = ctx.createLinearGradient(
              chartArea.left,
              0,
              chartArea.right,
              0
            );

            const cmap =
              colormaps[currentColormap] ||
              colormaps[defaultColormap] ||
              colormaps.speechStable;

            if (!cmap?.getColor) {
              return "rgba(100, 100, 100, 0.4)";
            }

            const fixedMin = 41;
            const fixedMax = 86;
            const fixedRange = fixedMax - fixedMin || 1;
            const steps = 100;

            for (let i = 0; i <= steps; i++) {
              const xPercent = i / steps;
              const xValue =
                voicedXMin + xPercent * (voicedXMax - voicedXMin);

              let closestIntensity = fixedMin;
              let minDistance = Infinity;

              for (let j = 0; j < intensityXs.length; j++) {
                const xi = Number(intensityXs[j]);
                const yi = Number(intensityYs[j] ?? fixedMin);
                const distance = Math.abs(xi - xValue);

                if (distance < minDistance) {
                  minDistance = distance;
                  closestIntensity = yi;
                }
              }

              const clampedIntensity = clamp(
                closestIntensity,
                fixedMin,
                fixedMax
              );
              const normalizedIntensity =
                (clampedIntensity - fixedMin) / fixedRange;
              const mappedIntensity = normalizedIntensity * 90;

              try {
                const color = cmap.getColor(mappedIntensity);
                const transparentColor = color
                  .replace("rgb(", "rgba(")
                  .replace(")", ", 0.4)");
                gradient.addColorStop(xPercent, transparentColor);
              } catch {
                gradient.addColorStop(xPercent, "rgba(100, 100, 100, 0.4)");
              }
            }

            return gradient;
          },
          datalabels: { display: false },
        },
      ],
    };
  }, [
    pitchXs,
    smoothedPitchYs,
    isMobile,
    showPitch,
    currentColormap,
    voicedXMin,
    voicedXMax,
    intensityXs,
    intensityYs,
  ]);

  const options = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      layout: { padding: { left: 8, right: 12, top: 8, bottom: 8 } },

      scales: {
        x: {
          type: "linear",
          min: voicedXMin,
          max: voicedXMax,
          grid: { drawBorder: false, color: "rgba(148,163,184,0.14)" },
          ticks: { display: false },
          title: { display: false },
        },
        y: {
          min: Math.max(0, yMin - yRange * 0.15),
          max: yMax * 1.05,
          grid: { drawBorder: false, color: "rgba(148,163,184,0.14)" },
          title: {
            display: true,
            text: "Pitch (Hz)",
            color: "rgba(51,65,85,0.80)",
            font: { size: isMobile ? 12 : 13, weight: "600" },
          },
          ticks: {
            color: "rgba(51,65,85,0.70)",
            font: { size: isMobile ? 10 : 11 },
            maxTicksLimit: 5,
          },
        },
      },

      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: !isMobile,
          mode: "nearest",
          intersect: false,
          callbacks: {
            afterBody: (context) => {
              const xValue = Number(context?.[0]?.parsed?.x);

              let closestIntensity = 0;
              let minDistance = Infinity;

              for (let i = 0; i < intensityXs.length; i++) {
                const xi = Number(intensityXs[i]);
                const yi = Number(intensityYs[i] ?? 0);
                const distance = Math.abs(xi - xValue);

                if (distance < minDistance) {
                  minDistance = distance;
                  closestIntensity = yi;
                }
              }

              return [`Intensity: ${closestIntensity.toFixed(1)} dB`];
            },
          },
        },
        datalabels: { display: false },
        cursorBallPlugin: {
          cursorX,
          cursorEnvelopePoint,
          ballRadius,
          showBall,
          isMobile,
        },
      },

      interaction: { mode: "nearest", axis: "x", intersect: false },
      elements: {
        point: { radius: 0, hoverRadius: 0, hitRadius: 0 },
      },
    };
  }, [
    isMobile,
    yMin,
    yMax,
    yRange,
    voicedXMin,
    voicedXMax,
    intensityXs,
    intensityYs,
    cursorX,
    cursorEnvelopePoint,
    ballRadius,
    showBall,
  ]);

  if (!pitchXs.length || !intensityXs.length) return null;

  return (
    <div className="flex flex-col gap-2 w-full min-w-0">
      <div className="flex flex-col gap-2">
        <label
          className={`flex items-center gap-2 select-none ${
            isMobile ? "text-xs" : "text-sm"
          }`}
        >
          Color Map:
          <select
            value={currentColormap}
            onChange={(e) => handleColormapChange(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          >
            {Object.entries(colormaps).map(([key, cm]) => (
              <option key={key} value={key}>
                {cm.name} - {cm.description}
              </option>
            ))}
          </select>
        </label>

        <ColorBar
          colormap={
            colormaps[currentColormap] ||
            colormaps[defaultColormap] ||
            colormaps.speechStable
          }
          min={41}
          max={86}
        />
      </div>

      <div
        className={`w-full min-w-0 overflow-x-auto bg-white rounded-lg border border-gray-200 p-2 ${
          isMobile ? "h-48" : "h-64"
        }`}
      >
        <Line data={data} options={options} plugins={[cursorBallPlugin]} />
      </div>
    </div>
  );
}